import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bell, Eye, Plus, Trash2, AlertTriangle } from "lucide-react";

const SEV_TONE = {
  high: "border-l-red-500 bg-red-50/40",
  medium: "border-l-amber-500 bg-amber-50/40",
  low: "border-l-teal-500 bg-teal-50/40",
};

export default function Alerts() {
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [alerts, setAlerts] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [watch, setWatch] = useState({ kind: "phone", value: "", label: "" });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: a }, { data: w }] = await Promise.all([
        api.get("/alerts"),
        api.get("/watchlist"),
      ]);
      setAlerts(a);
      setWatchlist(w);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markRead = async (alertId) => {
    await api.post(`/alerts/${alertId}/read`);
    setAlerts((prev) => prev.map((x) => (x.id === alertId ? { ...x, read: true } : x)));
    window.dispatchEvent(new Event("mukti-alerts-changed"));
  };
  const markAll = async () => {
    await api.post("/alerts/read-all");
    setAlerts((prev) => prev.map((x) => ({ ...x, read: true })));
    window.dispatchEvent(new Event("mukti-alerts-changed"));
    toast.success("All alerts marked read");
  };
  const addWatch = async (e) => {
    e.preventDefault();
    if (!watch.value.trim()) return;
    try {
      await api.post("/watchlist", watch);
      setWatch({ kind: "phone", value: "", label: "" });
      toast.success("Added to watchlist");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };
  const removeWatch = async (wid) => {
    await api.delete(`/watchlist/${wid}`);
    toast.success("Removed");
    fetchAll();
  };

  const unread = alerts.filter((a) => !a.read).length;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="alerts-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700 flex items-center gap-1.5"><Bell className="h-4 w-4" /> Notifications</div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Alerts</h1>
          <p className="text-sm text-slate-500 mt-1">New high-risk clusters and watch-listed identifiers surface here.</p>
        </div>
        {unread > 0 && <Button data-testid="mark-all-read" variant="outline" className="border-slate-300" onClick={markAll}>Mark all read ({unread})</Button>}
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <section className="space-y-3">
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : alerts.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500" data-testid="alerts-empty">No alerts yet.</div>
          ) : (
            alerts.map((a) => (
              <div key={a.id} data-testid={`alert-${a.id}`}
                className={`border border-slate-200 border-l-4 rounded-md p-4 bg-white ${SEV_TONE[a.severity] || SEV_TONE.medium} ${a.read ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-slate-500 shrink-0" />
                      <span className="font-medium text-sm">{a.title}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{a.detail}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-1">{(a.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.ad_id && <Link to={`/ads/${a.ad_id}`} className="text-xs text-blue-700 hover:underline">View ad</Link>}
                    {!a.read && <button data-testid={`alert-read-${a.id}`} onClick={() => markRead(a.id)} className="text-xs text-slate-500 hover:text-slate-900">Mark read</button>}
                  </div>
                </div>
              </div>
            ))
          )}
        </section>

        <aside>
          <section className="border border-slate-200 bg-white rounded-md p-5 space-y-3">
            <h2 className="font-display font-bold text-lg tracking-tight flex items-center gap-2"><Eye className="h-4 w-4" /> Watchlist</h2>
            <p className="text-xs text-slate-500">Get an alert whenever a watched identifier reappears in a new ad.</p>
            {!readOnly && (
              <form onSubmit={addWatch} className="space-y-2">
                <div className="flex gap-2">
                  <select data-testid="watch-kind" value={watch.kind} onChange={(e) => setWatch({ ...watch, kind: e.target.value })}
                    className="border border-slate-300 rounded-md text-sm px-2 py-2">
                    <option value="phone">Phone</option><option value="image">Image</option><option value="username">Username</option><option value="email">Email</option>
                  </select>
                  <Input data-testid="watch-value" placeholder="Value" value={watch.value} onChange={(e) => setWatch({ ...watch, value: e.target.value })} />
                </div>
                <Button data-testid="watch-add" type="submit" size="sm" className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5"><Plus className="h-4 w-4" /> Watch</Button>
              </form>
            )}
            <ul className="space-y-2 pt-1">
              {watchlist.map((w) => (
                <li key={w.id} data-testid={`watch-${w.id}`} className="flex items-center justify-between gap-2 text-sm border border-slate-100 rounded-md px-3 py-2 bg-slate-50">
                  <span className="min-w-0"><span className="text-[10px] uppercase font-mono text-slate-400 mr-1">{w.kind}</span><span className="font-mono truncate">{w.value}</span></span>
                  <button data-testid={`watch-remove-${w.id}`} onClick={() => removeWatch(w.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
              {watchlist.length === 0 && <li className="text-xs text-slate-400">Nothing watched yet.</li>}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
